import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import type { ControlValueAccessor, Validator } from '@angular/forms';
import { map, takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';

import {
  defaultProgramMaxCapacity,
  minimumProgramCapacity,
  sanitizeMatchCapacityForUpdate,
} from './program-participant-preferences-form.helper';
import { ProgramParticipantPreferencesFormValues } from './program-participant-preferences-form.model';
import { fadeInEnter } from '@libs/src/animations/animations';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import {
  ProgramAccount,
  ProgramMembership,
  ProgramRole,
  programRoleMentee,
  programRoleMentor,
  programRolePeer,
} from '@libs/src/models/program-membership.model';
import {
  Program,
  ProgramRoleOptionConfig,
  ProgramWithoutPopulatedSurvey,
  UserPreferencesPermission,
} from '@libs/src/models/program.model';
import {
  getCustomRoleFromUserProgramRole,
  getProgramRoles,
  getUserProgramRoleValue,
  isPeerProgram,
} from '@libs/src/program-memberships/program-membership.helper';
import { TtcSegmentedOption } from '@libs/src/ttc-segmented/ttc-segmented.types';
import { canUserManageProgram } from '@main-client/src/app/user/user.helpers';

type OnChangeFn = (value: ProgramParticipantPreferencesFormValues) => void;
type OnTouchedFn = () => void;

interface ProgramParticipantPreferencesForm {
  capacity: FormControl<number>;
  preferred_matches: FormControl<string[]>;
  roles: FormControl<ProgramRoleOptionConfig['value']>;
}

@Component({
  animations: [fadeInEnter],
  providers: [
    DestroySubscriptionsService,
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProgramParticipantPreferencesFormComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProgramParticipantPreferencesFormComponent),
    },
  ],
  selector: 'app-program-participant-preferences-form',
  templateUrl: './program-participant-preferences-form.component.html',
})
export class ProgramParticipantPreferencesFormComponent
  implements OnChanges, OnInit, ControlValueAccessor, Validator
{
  @Input() group: Group;
  @Input() program: Program | ProgramWithoutPopulatedSurvey;
  @Input() programAccounts: ProgramAccount[] = [];
  @Input() user: Account;
  @Input() userProgramMembership: ProgramMembership;
  programRoles: ProgramRoleOptionConfig[] = [];
  isPeerProgram: boolean;
  filteredAccounts: Account[];
  form: FormGroup<ProgramParticipantPreferencesForm>;
  userPreferencesPermissions: UserPreferencesPermission[];
  canUserManageProgram = false;
  readonly defaultProgramMaxCapacity = defaultProgramMaxCapacity;
  segmentedRoleOptions: TtcSegmentedOption[];
  selectedRoleOption: TtcSegmentedOption;

  onChange: OnChangeFn = () => {};
  onTouched: OnTouchedFn = () => {};

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: FormBuilder,
  ) {}

  get rolesControl() {
    return this.form.get('roles');
  }

  get capacityControl() {
    return this.form.get('capacity');
  }

  get value(): ProgramParticipantPreferencesFormValues {
    return this.form.getRawValue();
  }

  set value(value: ProgramParticipantPreferencesFormValues) {
    this.form.patchValue(value);
    this.emitChangedValue(value);
    this.onTouched();
  }

  get capacityControlEnabled() {
    return (
      (this.canUserManageProgram &&
        this.user._id !== this.userProgramMembership.account?._id) ||
      this.userPreferencesPermissions.includes('capacity')
    );
  }

  get preferredMatchesControlEnabled() {
    return (
      (this.canUserManageProgram &&
        this.user._id !== this.userProgramMembership.account?._id) ||
      this.userPreferencesPermissions.includes('preferred_matches')
    );
  }

  get rolesControlEnabled() {
    return (
      (this.canUserManageProgram &&
        this.user._id !== this.userProgramMembership.account?._id) ||
      this.isRoleSelectionEnabled
    );
  }

  get isRoleSelectionEnabled() {
    return this.userPreferencesPermissions.includes('roles');
  }

  ngOnInit() {
    this.programRoles = getProgramRoles(this.program);
    this.segmentedRoleOptions = this.programRoles.map(({ label, value }) => ({
      label,
      value,
      useTemplate: true,
    }));
    this.isPeerProgram = isPeerProgram(this.program);
    this.userPreferencesPermissions =
      this.program?.user_preferences_permissions || [];
    this.canUserManageProgram = canUserManageProgram(
      this.user,
      this.group,
      this.program,
    );
    let roleValue: string;
    if (this.isPeerProgram) {
      roleValue = programRolePeer;
    }
    const programPreferencesRoles =
      this.userProgramMembership?.program_preferences?.program_roles || [];
    if (
      !this.isPeerProgram &&
      (this.isRoleSelectionEnabled || 0 < programPreferencesRoles?.length)
    ) {
      roleValue = programPreferencesRoles?.length
        ? programPreferencesRoles?.join(',')
        : programRoleMentor;
    }
    this.form = this.fb.group({
      capacity: new FormControl(
        {
          disabled: !this.capacityControlEnabled,
          value:
            this.userProgramMembership?.program_preferences?.capacity ??
            minimumProgramCapacity,
        },
        [
          Validators.required,
          Validators.max(
            this.program?.max_match_capacity ?? defaultProgramMaxCapacity,
          ),
        ],
      ),
      preferred_matches: new FormControl({
        disabled: !this.preferredMatchesControlEnabled,
        value:
          this.userProgramMembership?.program_preferences?.preferred_matches ??
          [],
      }),
      roles: new FormControl(
        {
          disabled: !this.rolesControlEnabled,
          value: getCustomRoleFromUserProgramRole(
            roleValue,
            this.program.program_config?.primary_name,
            this.program.program_config?.secondary_name,
          ),
        },
        Validators.required,
      ),
    });
    this.form.valueChanges
      .pipe(
        map(() => this.form.getRawValue()),
        tap((formValue) => {
          this.emitChangedValue(formValue);
          this.onTouched();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.observeProgramRoleControlChange();
    this.selectedRoleOption = this.segmentedRoleOptions.find(
      ({ value }) => value === this.rolesControl.value,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.programAccounts?.currentValue) {
      return;
    }
    this.filteredAccounts = this.programAccounts
      .map(({ account }) => account)
      .filter(
        (account) => account._id !== this.userProgramMembership.account._id,
      );
  }

  registerOnChange(onChangeFunction: OnChangeFn) {
    this.onChange = onChangeFunction;
    if (this.value) {
      this.emitChangedValue(this.value);
    }
  }

  registerOnTouched(onTouchedFunction: OnTouchedFn) {
    this.onTouched = onTouchedFunction;
  }

  writeValue(value: ProgramParticipantPreferencesFormValues) {
    if (!value) {
      return;
    }
    this.value = value;
  }

  emitChangedValue(value: Partial<ProgramParticipantPreferencesFormValues>) {
    const { capacity, preferred_matches, roles } = value;
    const selectedRole = getUserProgramRoleValue(roles, this.programRoles, {
      isPeerProgram: this.isPeerProgram,
    });
    const calculatedCapacity =
      programRoleMentee === selectedRole ? minimumProgramCapacity : capacity;
    this.onChange({
      preferred_matches,
      capacity: calculatedCapacity,
      roles: selectedRole,
    });
  }

  validate(): ValidationErrors | null {
    const isFormValid = !Object.values(this.form.controls).some(
      (control) => control.invalid,
    );
    return isFormValid ? null : { preferences: { valid: false } };
  }

  observeProgramRoleControlChange() {
    this.rolesControl?.valueChanges
      .pipe(
        tap((value) => {
          const programRoleValue = getUserProgramRoleValue(
            value,
            this.programRoles,
            { isPeerProgram: this.isPeerProgram },
          );
          const calculatedCapacity = sanitizeMatchCapacityForUpdate(
            programRoleValue?.split(',') as ProgramRole[],
            this.userProgramMembership.program_preferences,
          );
          this.form.get('capacity').patchValue(calculatedCapacity);
          this.form
            .get('capacity')
            .setValidators([
              Validators.required,
              Validators.min(minimumProgramCapacity),
              Validators.max(
                this.program?.max_match_capacity ?? defaultProgramMaxCapacity,
              ),
            ]);
          this.form.get('capacity').updateValueAndValidity();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  updateRoleControl(selectedRole: TtcSegmentedOption) {
    this.rolesControl.patchValue(selectedRole.value);
  }
}
